import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Card,
	CardContent,
	Button,
	SearchBar,
	SimpleTabBar,
	Alert,
	AlertTitle,
	PageHeader,
	TableSkeleton
} from '../ui';
import { ScansTable, MetricTiles } from '../composite';
import { useScanListPage } from '../features/scans/useScanListPage';
import { ExposureTrend } from '../features/scans/ExposureTrend';
import { PATHS, ROUTE_PATTERNS, buildRoute } from '../../constants/Paths';

/**
 * ScanList - Redesigned page component using new architecture
 * 
 * Demonstrates separation of concerns:
 * - Business logic in useScanListPage hook
 * - UI rendering with design primitives
 * - Clean composition of components
 */
export const ScanListPage = () => {
	const navigate = useNavigate();

	const {
		scans,
		loading,
		error,
		stats,
		tabs,
		searchTerm,
		selectedPlatform,
		sortBy,
		activeTab,
		hasFilters,
		isEmpty,
		isFiltered,
		// Actions
		retryScan,
		cancelScan,
		updateSearch,
		updateSorting,
		updateTab,
		updatePlatformFilter,
		resetFilters,
		refresh,
	} = useScanListPage();

	// Sort options
	const sortOptions = [
		{ value: 'startedAt', label: 'Started Date' },
		{ value: 'clientName', label: 'Client Name' },
		{ value: 'status', label: 'Status' },
		{ value: 'platform', label: 'Platform' },
		{ value: 'riskScore', label: 'Risk Score' },
	];

	const handleCreateScan = () => {
		navigate(PATHS.NEW_SCANS);
	};

	return (
		<div className="min-h-screen bg-muted">
			<div className="max-w-7xl mx-auto p-6">
				{/* Header */}
				<PageHeader
					title="Scans"
					description="Monitor and manage your social media scans"
					actions={<>
						<Button variant="outline" onClick={refresh}>Refresh</Button>
						<Button onClick={handleCreateScan}>New Scan</Button>
					</>}
				/>

				{/* Stats Section */}
				<div className="mb-6">
						<MetricTiles
							items={[
								{ label: 'Total scans', value: stats.totalScans, icon: 'scans', accent: 'brand' },
								{ label: 'In progress', value: stats.inProgressScans, icon: 'progress', accent: 'brand' },
								{ label: 'Completed', value: stats.completedScans, icon: 'completed', accent: 'green' },
							]}
						/>
					</div>

				{/* Exposure lives here because it comes from scanning. It reads the
				    filtered set, so narrowing by platform or search narrows the chart. */}
				<ExposureTrend scans={scans} className="mb-6" />

				{/* Tabs */}
				<div className="mb-6">
					<SimpleTabBar
						tabs={tabs}
						activeTab={activeTab}
						onTabChange={updateTab}
						showCounts
						variant="underline"
					/>
				</div>

				{/* Search and Filters */}
				<Card className="mb-6">
					<CardContent className="p-6">
						<SearchBar
							searchTerm={searchTerm}
							onSearchChange={updateSearch}
							selectedPlatform={selectedPlatform}
							onPlatformFilter={updatePlatformFilter}
							sortBy={sortBy}
							onSort={updateSorting}
							sortOptions={sortOptions}
							searchPlaceholder="Search scans by client name, scan type, or ID..."
						/>

						{hasFilters && (
							<div className="mt-4 flex items-center gap-2">
								<span className="text-sm text-muted-foreground">Active filters:</span>
								<Button variant="outline" size="sm" onClick={resetFilters}>
									Clear all filters
								</Button>
							</div>
						)}
					</CardContent>
				</Card>

				{/* Error State */}
				{error && (
					<Alert variant="destructive" className="mb-6">
						<AlertTitle>Error loading scans</AlertTitle>
						<p>{error}</p>
					</Alert>
				)}

				{/* Loading State */}
				{loading && <TableSkeleton rows={5} />}

				{/* Empty State */}
				{!loading && isEmpty && (
					<Card>
						<CardContent className="text-center py-12">
							<h3 className="text-lg font-medium text-foreground mb-2">
								{isFiltered ? 'No scans match your filters' : 'No scans found'}
							</h3>
							<p className="text-muted-foreground mb-6">
								{isFiltered
									? 'Try adjusting your search criteria or filters'
									: 'Get started by creating your first scan'
								}
							</p>
							{isFiltered ? (
								<Button variant="outline" onClick={resetFilters}>
									Clear filters
								</Button>
							) : (
								<Button onClick={handleCreateScan}>
									Create Your First Scan
								</Button>
							)}
						</CardContent>
					</Card>
				)}

				{/* Scans Table */}
				{!loading && !isEmpty && (
					<ScansTable
						scans={scans}
						onViewScan={(scanId) => navigate(buildRoute(ROUTE_PATTERNS.SCAN_DETAIL, { id: scanId }))}
						onRetryScan={retryScan}
						onCancelScan={cancelScan}
						loading={loading}
					/>
				)}


			</div>
		</div>
	);
}; 